import { useState, useEffect } from 'react';
import { ArtistData } from '@/lib/artistData';
import { getSpotifyData } from '@/api/spotify';
import { Button } from '@/components/ui/button';
import { useTranslations } from '../hooks/useTranslations';
import InfiniteTrackScroll from './artists/InfiniteTrackScroll';

export interface SpotifyTrack {
  id: string;
  name: string;
  duration_ms: number;
  external_urls: {
    spotify: string;
  };
  album: {
    name: string;
    images: {
      url: string;
      height: number;
      width: number;
    }[];
    release_date: string;
  };
  artists: {
    name: string;
  }[];
  preview_url?: string | null;
}

interface ArtistMusicSectionProps {
  artist: ArtistData;
  onTrackSelect?: (track: SpotifyTrack) => void;
  accentColor?: string;
}

const ArtistMusicSection = ({ artist, onTrackSelect, accentColor = '#3B82F6' }: ArtistMusicSectionProps) => {
  const t = useTranslations();
  const [spotifyData, setSpotifyData] = useState<{
    latestTracks: SpotifyTrack[];
    topTracks: SpotifyTrack[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSpotifyData = async () => {
      try {
        setIsLoading(true);
        if (artist.spotifyArtistId) {
          console.log(`Fetching Spotify data for artist: ${artist.name} (ID: ${artist.spotifyArtistId})`);
          const data = await getSpotifyData(artist.spotifyArtistId);
          console.log(`Spotify data fetched for ${artist.name}:`, data);
          setSpotifyData(data);
        }
      } catch (error) {
        console.error('Error fetching Spotify data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpotifyData();
  }, [artist.spotifyArtistId, artist.name]);

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleTrackClick = (track: SpotifyTrack) => {
    if (onTrackSelect) {
      onTrackSelect(track);
    }
  };

  return (
    <div id="music" className="space-y-12 mb-16">
      {/* Latest Releases */}
      {spotifyData?.latestTracks && spotifyData.latestTracks.length > 0 && (
        <section>
          <h2 className="text-3xl font-bold mb-8 text-gray-900">{t.artistsPage?.music?.latestTracks || 'Senaste Låtarna'}</h2>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div 
                className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
                style={{ borderColor: '#A754FF' }}
              ></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {spotifyData.latestTracks.slice(0, 5).map((track) => (
                <div
                  key={track.id}
                  className="rounded-2xl p-4 border border-border-light bg-white hover:bg-slate-50 hover:border-indigo-200 hover:shadow-md transition-all duration-150 cursor-pointer group"
                  onClick={() => handleTrackClick(track)}
                >
                  <div className="aspect-square bg-slate-100 rounded-xl flex items-center justify-center mb-3 relative overflow-hidden">
                    {track.album.images && track.album.images.length > 0 ? (
                      <img
                        src={track.album.images[0].url}
                        alt={track.name}
                        className="absolute inset-0 w-full h-full object-cover rounded-xl"
                      />
                    ) : null}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-brand-indigo flex items-center justify-center">
                        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary truncate">{track.name}</h3>
                  <p className="text-xs text-text-secondary truncate">{track.album.name}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Top Tracks — Infinite Scroll */}
      {!isLoading && spotifyData?.topTracks && spotifyData.topTracks.length > 0 && (
        <InfiniteTrackScroll
          accentColor={accentColor}
          spotifyArtistUrl={artist.spotifyUrl}
          tracks={spotifyData.topTracks.slice(0, 10).map(track => ({
            id: track.id,
            name: track.name,
            artist: track.artists?.[0]?.name ?? artist.name,
            image: track.album.images?.[0]?.url ?? '',
            spotifyUrl: track.external_urls.spotify,
            duration: formatDuration(track.duration_ms),
          }))}
        />
      )}

      {artist.spotifyUrl && (
        <div className="text-center">
          <a href={artist.spotifyUrl} target="_blank" rel="noopener noreferrer">
            <Button 
              className="text-white px-8 py-4 rounded-xl text-lg transition-all hover:scale-105"
              style={{
                backgroundColor: '#A754FF',
                boxShadow: '0 4px 16px rgba(167, 84, 255, 0.3)'
              }}
            >
              {t.artistsPage?.music?.viewAllOnSpotify || 'Visa alla på Spotify'}
            </Button>
          </a>
        </div>
      )}
    </div>
  );
};

export default ArtistMusicSection;